// This wrapper is included on the parent app's page where they want to load the
// Checkout widget. This creates an <iframe> element and then sends a message to
// the iframe to render the Checkout widget with the passed options.
//
// NOTE: SOME CLIENTS MAY CACHE THIS WRAPPER, downloading a specific version and
// serving it from their own server instead of hot-linking to it from our
// server. BE CAREFUL to not break the parent+child interface.

import { GSG_ENVIRONMENT, baseGovHubUrl, initWidgetEnvWithFallback } from '@grantstreet/psc-environment/environment.js'
import { GSG_ENVIRONMENT_LEGACY } from '@grantstreet/psc-environment/environment-legacy-inject.js'
import iframeWhisperer from '@grantstreet/iframe-whisperer'
import VueCookies from 'vue-cookies'
import * as LDClient from 'launchdarkly-js-client-sdk'
import NonProdConfigAPI from '@grantstreet/psc-config/src/api/non-prod.js'
import ProdConfigAPI from '@grantstreet/psc-config/src/api/prod.js'
import RequestApi from '@grantstreet/request-api'
import { sentryException } from './sentry.ts'
import { isAxiosError } from 'axios'
import type CheckoutWidgetInternalType from './main-internal.ts'

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    CheckoutWidgetInternal: {
      attach: typeof CheckoutWidgetInternalType
    }
  }
}

let childInterface

export default async function attach (elSelector, {
  // These are passed directly through with the attach() iframe message:
  site: clientAndSite,
  environment,
  cartId,
  language,

  // This is a boolean that, if true, loads the Checkout Widget without an
  // iframe for use in the internal public site
  internal,

  // This is a boolean that, if true, loads the Checkout widget iframe via a
  // relative path instead of a fully-qualified URL. This lets us test changes
  // to the iframe in sandboxes.
  useLocalIframe,

  // These are functions and cannot be serialized and sent to the iframe. We
  // have to proxy them to the child as actions in this wrapper.
  getAccessToken,
  getUserData,
  handleLogin,
  onSuccess,
  // If this handler is passed we will show a Cancel button on the shopping cart
  // page that calls it when clicked.
  onCancel,
  onError,
}) {
  if (childInterface) {
    childInterface.destroy()
  }

  try {
    initWidgetEnvWithFallback({
      explicitEnvironment: environment,
      legacyEnvironmentOverride: GSG_ENVIRONMENT_LEGACY,
      diagApp: 'checkout-widget',
      diagSite: clientAndSite,
      RequestApi,
      exceptionLogger: sentryException,
      urlForLegacyEnvironment: undefined,
    })
  }
  catch (rawError) {
    let message
    if (!(rawError instanceof Error)) {
      message = String(rawError)
    }
    else {
      message = rawError.message
    }
    const error = new Error(`Checkout widget error: ${message}`)
    handleError(error, onError, elSelector, language)
    return
  }

  // Parse the client and site out of the passed 'ohio/ems'-formatted `site`
  const [ , client, site ] = clientAndSite.replace(/\s/g, '')
    .match(/^([^/]+)\/([^/]+)$/) || []
  if (!client || !site) {
    handleError(`Checkout widget error: you must pass a "site" parameter formatted like "clientId/siteId". Passed value: "${clientAndSite}"`, onError, elSelector, language)
    return
  }

  if (language !== 'en' && language !== 'es') {
    console.error(`Checkout widget error: invalid language value "${language}". Supported values "en", "es". Defaulting to "en".`)
    language = 'en'
  }

  const loadInternalScript = async () => {
    const existingScript = Array.from(document.querySelectorAll('script')).find(script => /\/checkout-internal\.js/.test(script.src))
    if (existingScript) {
      return
    }

    const currentScript = Array.from(document.querySelectorAll('script')).find(script => /\/checkout\.js/.test(script.src))

    if (!currentScript) {
      handleError('Wasn\'t able to determine checkout widget script origin', onError, elSelector, language)
      return
    }

    const internalUrlBase = currentScript.src.replace(/checkout\.js/, 'checkout-internal')
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = `${internalUrlBase}.css`
    const script = document.createElement('script')
    script.src = `${internalUrlBase}.js`
    const loadPromise = new Promise((resolve, reject) => {
      script.onload = resolve
      script.onerror = reject
    })

    const overrideTheme = document.querySelector('#override-theme')

    if (overrideTheme) {
      document.head.insertBefore(link, overrideTheme)
    }
    else {
      document.body.appendChild(link)
    }

    document.body.appendChild(script)

    return loadPromise
  }

  if (internal) {
    try {
      await loadInternalScript()

      window.CheckoutWidgetInternal.attach(elSelector, {
        client,
        site,
        environment: GSG_ENVIRONMENT,
        cartId,
        language,
        getAccessToken,
        getUserData,
        handleLogin,
        onSuccess,
        onCancel,
        topLevelDomain: window?.location?.hostname,
        showCancelEmitButton: Boolean(onCancel),
        onLoadFailure: (errorMessage) => {
          handleError(`Checkout widget error: ${errorMessage}`, undefined, elSelector)
        },
      })
    }
    catch (error) {
      let message
      if (!(error instanceof Error)) {
        message = String(error)
      }
      else {
        message = error.message
      }

      console.error(error)

      handleError(`There was an unexpected error attaching the internal checkout widget: ${message}`, onError, elSelector, language)
    }

    return
  }

  // Check LaunchDarkly to see if this user should use the Checkout widget or
  // the old redirect-to-PayHub method (e.g., for a percentage-based rollout)
  if (!await useCheckoutWidget(client, site)) {
    redirectToPayHub(client, site, cartId)
    return
  }

  // Sandboxes use a relative iframe URL in order to be able to test any iframe
  // changes. Otherwise we use a full URL because this wrapper will be embedded
  // on client websites.
  const iframeUrl = useLocalIframe
    ? './checkout-iframe.html'
    : `${baseGovHubUrl}/web/widgets/checkout-iframe.html`

  const iframe = document.createElement('iframe')
  iframe.setAttribute('src', iframeUrl)
  iframe.setAttribute('name', 'checkout-widget')
  iframe.setAttribute('allow', 'payment https://beta.govhub.com https://test.govhub.com https://govhub.com https://test.pay-hub.net https://beta.pay-hub.net https://pay-hub.net')
  iframe.style.cssText = `
    width: 100%;
    height: 100px;
    border: 1px solid #EAEAEA;
    border-radius: 10px;
    box-sizing: border-box;
  `

  loadElement(iframe, elSelector)

  childInterface = new iframeWhisperer.ToChild({
    iframe,
    actions: {
      // BE CAREFUL when modifying this interface. See disclaimer at top and
      // legacy handlers below.
      'gsgPublicSite.getAccessToken': function () {
        if (getAccessToken) {
          return getAccessToken()
        }
      },
      'gsgPublicSite.getUserData': function () {
        if (getUserData) {
          return getUserData()
        }
      },
      'gsgPublicSite.handleLogin': function () {
        if (handleLogin) {
          return handleLogin()
        }
      },
      'gsgCheckout.cancelCart': function (_transaction) {
        onCancel()
      },
      'gsgCheckout.success': function (transaction) {
        onSuccess(transaction)
      },
      // If the iframe encounters an unrecoverable error:
      'gsgCheckout.loadFailure': function (errorMessage) {
        handleError(`Checkout widget error: ${errorMessage}`, undefined, elSelector)
      },

      // LEGACY HANDLERS
      // If you want to change the key of one of the above handlers, you should
      // copy the old handler down here to ensure the widget will still function
      // if the parent wrapper is cached.
      'gsgCheckout.receiptContinue': function (transaction) {
        onSuccess(transaction)
      },
    },
  })

  childInterface.notify({
    action: 'gsgCheckout.attach',
    payload: {
      client,
      site,
      environment: GSG_ENVIRONMENT,
      topLevelDomain: window?.location?.hostname,
      cartId,
      language,
      // If any onCancel handler was passed, show the cancel button
      showCancelEmitButton: Boolean(onCancel),
    },
  })
}

// Returns true if LaunchDarkly says the user should use the Checkout widget or
// if the site is configured to not use LaunchDarkly. If there are any issues
// getting the site setting, this returns true.
//
// Note, we intentionally do this manually in this parent wrapper instead of
// inside of the iframe (where we could use the PayHub LaunchDarkly and Site
// Settings helpers). This is in order to avoid any issues that might occur
// during the iframe loading process. If the iframe fails to load because of
// some cross-domain issue, we want to be able to redirect users to PayHub to
// use the old redirect checkout process while we fix the issue.
async function useCheckoutWidget (client, site) {
  // First, get the "useLaunchDarkly" Site Setting. This controls whether or not
  // we should ever download LaunchDarkly flags for this site. If this is
  // explicitly set to false, we return false early without grabbing the
  // LaunchDarkly flag. If the useLaunchDarkly setting was never published for
  // this site or there is an issue with the config service, we proceed and use
  // LaunchDarkly.
  const api = GSG_ENVIRONMENT === 'prod'
    ? new ProdConfigAPI({ exceptionLogger: sentryException })
    : new NonProdConfigAPI({ exceptionLogger: sentryException })
  try {
    const module = 'payHub'
    const settingName = 'useLaunchDarkly'

    const res = await api.getSiteSetting({
      clientId: client,
      siteId: site,
      module,
      setting: settingName,
    })

    const value = res?.data?.[`/payhub/data-pub/settings/${client}/${site}/${module}/${settingName}/value`]
    if (value === false) {
      // If useLaunchDarkly is explicitly set to false, don't consult
      // LaunchDarkly and instead assume all users should use the Checkout
      // widget (returning true)
      return true
    }
  }
  catch (error_) {
    if (!isAxiosError(error_) || error_.response?.status !== 404) {
      let message
      if (!isAxiosError(error_) && !(error_ instanceof Error)) {
        message = String(error_)
      }
      else {
        message = error_.message
      }
      const error = new Error(`Checkout widget error: could not get useLaunchDarkly Site Setting for ${client}/${site}: ${message}. Proceeding to use LaunchDarkly.`)
      sentryException(error)
      console.error(error)
    }
  }

  // Now that we have decided to use LaunchDarkly, download and return the flag
  const id = GSG_ENVIRONMENT === 'prod'
    ? '5cbb2d789e851908187acbeb'
    : '5cbb2d789e851908187acbea'

  // If we can determine the user's IP, use it to identify the user with
  // LaunchDarkly. Otherwise use "anonymous" to let LD generate a UUID.
  // @ts-expect-error Ignoring typescript error because VueCookies is typed
  // wrong. See: https://github.com/cmp-cc/vue-cookies/issues/76
  const ip = VueCookies.get('client_ip')
  const userContextData = ip ? { kind: 'user', key: ip } : { kind: 'user', anonymous: true }

  try {
    const ldClient = LDClient.initialize(id, userContextData)
    await ldClient.waitForInitialization()

    // This variation can be `undefined` if the flag does not exist. In that case
    // we want the user to use the Checkout widget. We only return false if the
    // flag exists and is false.
    return ldClient.variation(`use-checkout-widget.${client}-${site}`) !== false
  }
  catch (error) {
    if (!(error instanceof Error)) {
      return sentryException(new Error('Unknown error occurred while initializing LaunchDarkly: ' + String(error)))
    }

    if (
      error.name === 'LaunchDarklyFlagFetchError' &&
      error.message.match(/network error|Failed to execute 'send' on 'XMLHttpRequest'/)
    ) {
      // User navigated away before the LD request could complete.
      return
    }

    sentryException(error)
  }
}

function redirectToPayHub (client, site, cartId) {
  window.location.replace(`${baseGovHubUrl}/${client}/${site}/redirect/${cartId}`)
}

// Reports the error to the parent app via their onError() handler and then
// renders a generic error in the target element.
function handleError (message, onError, elSelector, language = 'en') {
  const error = new Error(message)
  console.error(message)
  sentryException(error)

  const errorMessage = {
    en: 'There was an issue loading the shopping cart. Please reload the page or try again later.',
    es: 'Hubo un problema al cargar el carrito de compras. Vuelva a cargar la página o inténtalo más tarde.',
  }

  const errorEl = document.createElement('div')
  errorEl.innerText = errorMessage[language]
  errorEl.setAttribute('style', `
    color: #be2c37;
    width: 100%;
    padding: 20px;
    text-align: center;
    box-sizing: border-box;
  `)

  loadElement(errorEl, elSelector)

  if (onError) {
    onError(error)
  }
}

function loadElement (element, elSelector) {
  const parentEl = document.querySelector(elSelector)
  parentEl.innerHTML = ''
  parentEl.appendChild(element)
}
